import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

// Lightweight "typing…" indicator.
//
// Cost profile: no HTTP requests, no DB writes. We only emit a WebSocket
// frame when the user *transitions* into typing (and at most once every
// 2s while they keep going), plus one "stopped" frame. The message form is
// re-rendered by Turbo on every send, so we listen via delegation on
// document to survive those replacements.
export default class extends Controller {
  static values = {
    conversationId: Number,
    userId: Number
  }

  connect () {
    this.typing = false
    this.lastSentAt = 0
    this.stopTimer = null
    this.hideTimer = null

    this.onInput = (event) => this.handleInput(event)
    this.onSubmit = (event) => this.handleSubmit(event)
    document.addEventListener('input', this.onInput)
    document.addEventListener('submit', this.onSubmit, true)

    this.subscription = consumer.subscriptions.create(
      { channel: 'ConversationChannel', id: this.conversationIdValue },
      { received: (data) => this.received(data) }
    )
  }

  disconnect () {
    document.removeEventListener('input', this.onInput)
    document.removeEventListener('submit', this.onSubmit, true)
    if (this.typing) this.sendTyping(false)
    if (this.stopTimer) clearTimeout(this.stopTimer)
    if (this.hideTimer) clearTimeout(this.hideTimer)
    if (this.subscription) this.subscription.unsubscribe()
  }

  handleInput (event) {
    const field = event.target
    if (!field || field.name !== 'message[body]') return
    if (!field.closest('#message-form')) return

    if (field.value.trim() === '') {
      this.stop()
      return
    }

    const now = Date.now()
    if (!this.typing || now - this.lastSentAt > 2000) {
      this.typing = true
      this.lastSentAt = now
      this.sendTyping(true)
    }

    if (this.stopTimer) clearTimeout(this.stopTimer)
    this.stopTimer = setTimeout(() => this.stop(), 3000)
  }

  handleSubmit (event) {
    const form = event.target
    if (!form || form.id !== 'message-form') return
    this.stop()
  }

  stop () {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer)
      this.stopTimer = null
    }
    if (!this.typing) return
    this.typing = false
    this.sendTyping(false)
  }

  sendTyping (state) {
    if (!this.subscription) return
    this.subscription.perform('typing', { typing: state })
  }

  received (data) {
    if (!data || data.type !== 'typing') return
    if (Number(data.user_id) === this.userIdValue) return

    const indicator = document.getElementById('chat-typing-indicator')
    if (!indicator) return

    if (data.typing) {
      const label = indicator.querySelector('[data-typing-name]')
      if (label && data.user_name) label.textContent = data.user_name
      indicator.classList.remove('hidden')
      if (this.hideTimer) clearTimeout(this.hideTimer)
      // Safety net in case a "stopped" frame is ever dropped.
      this.hideTimer = setTimeout(() => indicator.classList.add('hidden'), 5000)
    } else {
      indicator.classList.add('hidden')
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    }
  }
}
